import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { ChatArea } from 'src/components/ChatArea';
import { useAppDispatch, useAppSelector } from 'src/redux/redux-store/hooks';
import {
  BackgroundImage,
  Button,
  Card,
  Center,
  Container,
  LoadingOverlay,
  Skeleton,
  Text,
  Tooltip,
  Transition,
  UnstyledButton,
  createStyles,
  useMantineTheme,
} from '@mantine/core';
import { StyledContainer } from 'src/components/common/StyledComponents/Container';
import { UserRole } from 'src/enums/User';
import { Navigate, useNavigate } from 'react-router-dom';
import { getRole } from 'src/utils/storage';
import {
  clearChatState,
  handleUpdateAppSettings,
  handleUserUpdate,
  handleWalkthroughUpdate,
  resetUser,
  stopStream,
  updateAzureToken,
} from 'src/redux/redux-store';
import {
  ChatStyledContainerInset,
  StyledContainerInset,
} from 'src/components/common/StyledComponents/BackgroundContainer';
import { COLORS } from 'src/utils/styles';
import { Notifications, showNotification } from '@mantine/notifications';
import ChatInput from 'src/components/common/Inputs/ChatMessageInput';
import { useChat } from 'src/hooks/useChat';
import { useNotifications } from 'src/hooks/useNotifications';
import {
  NotificationTypes,
  PubSubNotificationTypes,
} from 'src/enums/Notifications';
import useResponsive from 'src/hooks/useResponsive';
import { userRoutes } from 'src/navigators/routes/user';
import {
  sendFailureNotification,
  sendFeedbackNotification,
} from 'src/utils/notifications';
import Icon from 'src/components/common/Icons/Icons/Icon';
import { colors } from '@mui/material';
import { Disclaimer } from 'src/components/Chat/Disclaimer';
import { useFileUpload } from 'src/hooks/useFileUpload';
import { ChildWalkthrough } from 'src/components/Walkthroughs/child';
import { authService } from 'src/services/services/auth/auth.api';
import { ResponseKind } from 'src/services/services/MainService';
import { authRoutes } from 'src/navigators/routes/auth';
import { useAuth } from 'src/hooks/useAuth';
import DynamicLayout from 'src/components/Layout/DynamicLayout';
import { CustomButton } from 'src/components/common/Buttons/CustomButton';
import TopNavbar from 'src/components/TopNavbar';
import Sidebar from 'src/components/SideBar';
import SubjectDropdown from 'src/components/Chat/SubjectDropdown';

const Dashboard = React.memo(() => {
  const dispatch = useAppDispatch();
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const sessionId = useAppSelector((state) => state.chat.selectedSession);
  const appSettings = useAppSelector((state) => state.app).appSettings;
  const [loading, setLoading] = useState(true);
  const { newSession } = useChat();
  const user = useAppSelector((state) => state.user);
  const sessionsLoading = useAppSelector((state) => state.chat.sessionsLoading);
  const { backgroundColor, backgroundColorExterior, isMobile, isDesktop } =
    useResponsive();
  const {
    uploadStatus,
    uploadedFile,
    setUploadedFile,
    uploadFile,
    client,
    setUploadStatus,
  } = useFileUpload();
  const {
    inputMessage,
    setInputMessage,
    sendMessage,
    getMessages,
    messages,
    question_mode,
    chatLoading,
    streaming,
    suggestQuestion,
    triggerMessageForFileUpload,
    triggerMessageForFileUploadError,
    getSessions,
    activeSubject,
    setActiveSubject,
    subjects,
    fetchSubjects,
  } = useChat();
  const [key, setKey] = useState(0);
  const { logout } = useAuth();

  // Add state for page loading
  const [pageLoading, setPageLoading] = useState(true);

  // Add loading effect
  useEffect(() => {
    const allLoaded = !sessionsLoading && !chatLoading;
    setPageLoading(!allLoaded);
  }, [sessionsLoading, chatLoading]);

  // Initialize chat when component mounts
  useEffect(() => {
    const initializeChat = async () => {
      try {
        // First, fetch user data and app settings
        await Promise.all([
          dispatch(handleUserUpdate()),
          dispatch(handleUpdateAppSettings()),
        ]);

        // Then fetch subjects
        await fetchSubjects();
      } catch (error) {
        console.error('Error initializing chat:', error);
        sendFailureNotification(
          NotificationTypes.chatSession,
          'Failed to load initial data. Please refresh the page.'
        );
      }
    };

    initializeChat();
  }, []);

  // Handle session loading when activeSubject changes
  useEffect(() => {
    const loadSessions = async () => {
      if (!activeSubject) return;

      try {
        console.log('Loading sessions for subject:', activeSubject);
        const session = await getSessions();
        console.log('Sessions loaded, selected session:', session?.id);
      } catch (error) {
        console.error('Error loading sessions:', error);
        sendFailureNotification(
          NotificationTypes.chatSession,
          'Failed to load chat sessions.'
        );
      }
    };

    loadSessions();
  }, [activeSubject]);

  // Handle message loading when selected session changes
  useEffect(() => {
    const loadMessages = async () => {
      if (!sessionId?.id) return;

      try {
        console.log('Loading messages for session:', sessionId.id);
        await getMessages(sessionId.id);
      } catch (error) {
        console.error('Error loading messages:', error);
        sendFailureNotification(
          NotificationTypes.chatSession,
          'Failed to load messages.'
        );
      }
    };

    loadMessages();
  }, [sessionId?.id]);

  const handleSend = async (message: string) => {
    let idSession = sessionId;
    let read_url: string | undefined;
    if (uploadedFile) {
      try {
        triggerMessageForFileUpload(message, uploadedFile);
        const res = await uploadFile(uploadedFile, idSession.id);
        if (!res) {
          throw new Error('File upload failed');
        }
        read_url = res;
      } catch (error) {
        console.error('Error during file upload:', error);
        triggerMessageForFileUploadError(message);
        setUploadStatus('idle');
        setUploadedFile(null);
        return; // Exit the function if file upload failed
      }
    }

    // Proceed with sending the message
    try {
      await sendMessage(message, read_url);
      setUploadedFile(null);
      setInputMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      sendFailureNotification(
        NotificationTypes.chatSession,
        'Failed to send message. Please try again.'
      );
    } finally {
      setUploadStatus('idle');
    }
  };

  // Effect for handling app settings updates
  useEffect(() => {
    if (client) {
      const handleServerMessage = (data: any) => {
        if (data.message.data) {
          const message = JSON.parse(data.message.data.toString());
          if (message.event_type === PubSubNotificationTypes.appInfoUpdate) {
            dispatch(handleUpdateAppSettings());
          }
        }
      };

      client.on('server-message', handleServerMessage);
      return () => {
        client.off('server-message', handleServerMessage);
      };
    }
  }, [client, dispatch]);

  const handleSuggestQuestion = useCallback(
    (option: boolean) => {
      if (option) {
        suggestQuestion(true, sessionId.id);
      } else {
        suggestQuestion(false, sessionId.id);
      }
    },
    [sessionId, suggestQuestion]
  );

  // Helper type guard
  function isSubjectObject(s: any): s is { name: string } {
    return typeof s === 'object' && s !== null && 'name' in s;
  }

  const header = (
    <div className="flex flex-row w-full justify-between">
      <div className="flex flex-row items-center gap-2">
        <Icon name="chat" className="size-6 md:size-8" />
        <Text>{sessionId.title}</Text>
      </div>
      <div className="flex flex-row items-center gap-2">
        <SubjectDropdown
          subjects={
            Array.isArray(subjects) &&
            subjects.length > 0 &&
            isSubjectObject((subjects as any[])[0])
              ? (subjects as any[]).map((s) =>
                  isSubjectObject(s) ? s.name : s
                )
              : subjects
          }
          activeSubject={
            activeSubject ||
            (Array.isArray(subjects) && subjects.length > 0
              ? isSubjectObject((subjects as any[])[0])
                ? (subjects as any[])[0].name
                : (subjects as any[])[0]
              : '')
          }
          onSubjectChange={setActiveSubject}
        />
        <Tooltip label="New Chat">
          <div
            onClick={() => newSession()}
            className="new-chat-step bg-primary text-white text-base font-semibold rounded-lg full py-1 px-4 hover cursor-pointer flex items-center gap-x-1"
          >
            <Icon
              name="edit"
              color="white"
              size="24px"
              className="-mt-[2px] -ml-[1.5px]"
            />
            New Chat
          </div>
        </Tooltip>
      </div>
    </div>
  );

  useEffect(() => {
    if (user.walkthrough_completed) {
      setKey((prevKey) => prevKey + 1);
    }
  }, [user.walkthrough_completed]);

  return (
    <DynamicLayout
      key={key}
      heading={header}
      rightContent={
        <>
          <SubjectDropdown
            subjects={
              Array.isArray(subjects) &&
              subjects.length > 0 &&
              isSubjectObject((subjects as any[])[0])
                ? (subjects as any[]).map((s) =>
                    isSubjectObject(s) ? s.name : s
                  )
                : subjects
            }
            activeSubject={
              activeSubject ||
              (Array.isArray(subjects) && subjects.length > 0
                ? isSubjectObject((subjects as any[])[0])
                  ? (subjects as any[])[0].name
                  : (subjects as any[])[0]
                : '')
            }
            onSubjectChange={setActiveSubject}
          />
          <UnstyledButton
            className="bg-secondary w-6 h-6 rounded-md p-[5px]"
            onClick={() => newSession()}
          >
            <img src="/icons/newChat.png" alt="edit" />
          </UnstyledButton>
        </>
      }
      SidebarComponent={Sidebar}
    >
      <StyledContainer
        theme={{ backgroundColor: backgroundColorExterior }}
        className="flex flex-col h-[calc(100%-55px)] md:h-[calc(100%-70px)]"
      >
        <ChatStyledContainerInset
          theme={{ backgroundColor: backgroundColor }}
          className="flex-1 flex flex-col min-h-0"
        >
          <BackgroundImage
            className="flex-1 flex flex-col min-h-0"
            src="/images/doodles.png"
          >
            <Notifications
              position={isMobile ? 'top-center' : 'bottom-right'}
              limit={isMobile ? 1 : 5}
            />
            <ChildWalkthrough
              startWalkthrough={!user.walkthrough_completed && !isMobile}
              onCloseWalkthrough={() => {
                dispatch(handleWalkthroughUpdate());
              }}
            />

            {/* <ErrorMessage error={error ? error : ""} handleClose={dismissError} />  */}
            {/* <LoadingOverlay
              overlayBlur={10}
              visible={chatLoading || sessionsLoading}
            /> */}
            <div
              className={`flex-1 flex flex-col min-h-0 w-full lg:w-[700px] xl:w-[85%] py-0 md:py-2 mx-auto`}
            >
              {pageLoading ||
              (subjects.length > 0 && (chatLoading || sessionsLoading)) ? (
                <>
                  {/* Chat Area Skeleton */}
                  <div className="flex-1 relative min-h-0">
                    <div className="h-full overflow-y-auto py-8 px-2 space-y-8">
                      {/* User message skeleton */}
                      <div className="flex justify-end gap-3">
                        <div className="max-w-[80%] md:max-w-[70%] lg:max-w-[60%] xl:max-w-[50%] w-full">
                          <div className="bg-primary/10 p-4 rounded-t-2xl rounded-l-2xl">
                            <Skeleton height={16} radius="md" width="80%" />
                            <Skeleton
                              height={16}
                              radius="md"
                              width="60%"
                              mt={8}
                            />
                          </div>
                        </div>
                        <Skeleton height={40} width={40} circle />
                      </div>

                      {/* AI message skeleton */}
                      <div className="flex flex-col">
                        <div className="flex gap-3 max-w-[90%] md:max-w-[80%] lg:max-w-[70%] xl:max-w-[60%]">
                          <Skeleton height={40} width={40} circle />
                          <div className="flex-1">
                            <div className="bg-gray-100 p-4 rounded-r-2xl rounded-bl-2xl">
                              <Skeleton height={16} radius="md" width="90%" />
                              <Skeleton
                                height={16}
                                radius="md"
                                width="70%"
                                mt={8}
                              />
                              <Skeleton
                                height={16}
                                radius="md"
                                width="60%"
                                mt={8}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Loading indicator */}
                      <div className="flex justify-center py-4">
                        <div className="flex items-center gap-2">
                          <Skeleton height={12} width={12} circle />
                          <Skeleton height={12} width={12} circle />
                          <Skeleton height={12} width={12} circle />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Input Skeleton */}
                  <div className="flex-shrink-0 pb-safe px-2 pt-2">
                    <div className="flex gap-2 items-center">
                      <Skeleton height={44} radius="xl" className="flex-1" />
                      <Skeleton
                        height={44}
                        width={44}
                        radius="xl"
                        className="p-2"
                      >
                        <Skeleton height={24} width={24} radius="md" />
                      </Skeleton>
                      <Skeleton
                        height={44}
                        width={44}
                        radius="xl"
                        className="p-2"
                      >
                        <Skeleton height={24} width={24} radius="md" />
                      </Skeleton>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-1 relative min-h-0">
                    <ChatArea
                      sendMessage={sendMessage}
                      username={user.name}
                      messages={messages}
                      streaming={streaming}
                      subjects={subjects}
                    />
                  </div>

                  <div className="flex items-center px-0 md:px-4 mb-2">
                    <div className="w-[100%]">
                      <Transition
                        mounted={question_mode}
                        transition="fade"
                        duration={500}
                        timingFunction="ease"
                      >
                        {(styles) => (
                          <Card style={styles}>
                            <div className="flex flex-row gap-4 items-center justify-center">
                              {/* <Text>Do you want to test your knowledge?</Text> */}
                              <Card
                                className="bg-primary"
                                w={'100%'}
                                onClick={() => {
                                  handleSuggestQuestion(false);
                                }}
                                style={{ cursor: 'pointer' }}
                                withBorder
                                py={4}
                              >
                                <div className="flex flex-row gap-1 justify-between items-center">
                                  <Text style={{ color: 'white' }} size={'sm'}>
                                    Continue asking more questions
                                  </Text>
                                  <Icon
                                    color="white"
                                    name="question"
                                    size="1.1rem"
                                  />
                                </div>
                              </Card>
                              <Card
                                w={'100%'}
                                onClick={() => {
                                  handleSuggestQuestion(true);
                                }}
                                style={{ cursor: 'pointer' }}
                                bg={COLORS.primary}
                                withBorder
                                py={4}
                              >
                                <div className="flex flex-row gap-1 justify-between items-center">
                                  <Text size={'sm'}>Test my knowledge</Text>
                                  <Icon name="qa" size="1.1rem" />
                                </div>
                              </Card>
                            </div>
                          </Card>
                        )}
                      </Transition>
                      <div className="flex-shrink-0 pb-safe px-2 pt-2">
                        <ChatInput
                          handleUpgrade={() =>
                            sendFeedbackNotification(
                              NotificationTypes.accountUpgrade,
                              'Ask your parent to upgrade your plan.'
                            )
                          }
                          inputMessage={inputMessage}
                          access={user.trial_expired ? 'Free' : 'Premium'}
                          setInputMessage={setInputMessage}
                          onSend={handleSend}
                          uploadedFile={uploadedFile}
                          setUploadedFile={setUploadedFile}
                          uploadStatus={uploadStatus}
                          streaming={streaming}
                          disabled={streaming || !inputMessage || question_mode}
                        />
                      </div>
                      <Disclaimer text={appSettings?.child_disclaimers} />
                    </div>
                  </div>
                </>
              )}
            </div>
          </BackgroundImage>
        </ChatStyledContainerInset>
      </StyledContainer>
    </DynamicLayout>
  );
});

export default Dashboard;
